import { FC, useRef, useState, useCallback } from 'react';
import { Box, Card, CardContent, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

interface InfoSlide {
  title: string;
  content: string;
}

interface StartPageSwipableInfomrationProps {
}

export const StartPageSwipableInfomration: FC<StartPageSwipableInfomrationProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const infoSlides: InfoSlide[] = [
    {
      title: 'What you\'ll get',
      content: 'A personalized list of top study paths, step by step.',
    },
    {
      title: 'How it works',
      content: 'You\'ll see statements. Swipe to show how much you agree.',
    },
    {
      title: 'Why it matters',
      content: 'Your answers unlock Disciplines → Majors → Universities.',
    },
    {
      title: 'How long it takes',
      content: 'Just 30 swipes, about 3 minutes.',
    },
  ];

  const getActualCardWidth = useCallback(() => {
    if (!scrollContainerRef.current) return 0;

    const container = scrollContainerRef.current;
    const containerWidth = container.offsetWidth;
    const calculatedWidth = containerWidth * 0.85;
    const maxWidth = 400;

    return Math.min(calculatedWidth, maxWidth);
  }, []);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollPosition = container.scrollLeft;
    const cardWidth = getActualCardWidth();
    const newIndex = Math.round(scrollPosition / cardWidth);

    if (newIndex !== currentIndex) {
      setCurrentIndex(newIndex);
    }
  };

  const scrollToIndex = (index: number) => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const cardWidth = getActualCardWidth();
    const scrollPosition = index * cardWidth;

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
  };

  const handlePrevious = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
    setCurrentIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = Math.min(infoSlides.length - 1, currentIndex + 1);
    scrollToIndex(newIndex);
    setCurrentIndex(newIndex);
  };

  return (
    <Box position="relative" width="100%">
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          px: 2,
          py: 1,
          boxSizing: 'border-box',
          ...(!isMobile && ({
            '-webkit-mask-image': 'linear-gradient(to right, transparent 0% , rgba(0,0,0,1) 16px, rgba(0,0,0,1) calc(100% - 16px), transparent 100%)',
            '-webkit-mask-repeat': 'no-repeat',
          })),
        }}
      >
        {infoSlides.map((slide, idx) => (
          <Box
            key={idx}
            sx={{
              flexShrink: 0,
              width: '85%',
              maxWidth: 400,
              scrollSnapAlign: 'center',
              pr: 2,
              pb: 1, // Add padding at the bottom to prevent shadow from being cut off
              '&:first-of-type': {
                pl: 0,
              },
              '&:last-of-type': {
                pr: 0,
              },
            }}
          >
            <Card
              variant="elevation"
              elevation={2} // Increased elevation for more visible shadow
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                backgroundColor: 'background.paper',
                margin: '1px', // Small margin to ensure shadow is visible
              }}
            >
              <CardContent sx={{ py: 4 }}>
                <Typography 
                  variant="h5" 
                  fontWeight="bold" 
                  color="primary.main" 
                  gutterBottom
                  fontSize={24}
                >
                  {slide.title}
                </Typography>
                <Typography 
                  variant="body1" 
                  color="secondary.dark"
                  fontSize={20}
                >
                  {slide.content}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {!isMobile && infoSlides.length > 1 && (
        <>
          <IconButton
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            sx={{
              position: 'absolute',
              left: -30,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'background.paper',
              boxShadow: 2,
              zIndex: 1,
              padding: 1,
              '&:hover': {
                backgroundColor: 'background.paper',
                boxShadow: 3,
              },
              '&.Mui-disabled': {
                backgroundColor: 'action.disabledBackground',
              },
            }}
            size="medium"
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={handleNext}
            disabled={currentIndex === infoSlides.length - 1}
            sx={{
              position: 'absolute',
              right: -30,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'background.paper',
              boxShadow: 2,
              zIndex: 1,
              padding: 1,
              '&:hover': {
                backgroundColor: 'background.paper',
                boxShadow: 3,
              },
              '&.Mui-disabled': {
                backgroundColor: 'action.disabledBackground',
              },
            }}
            size="medium"
          >
            <ChevronRight />
          </IconButton>
        </>
      )}
    </Box>
  );
};
